import { isFunction, snakeCase } from "lodash";
import PropTypes from "prop-types";
import { clsx } from "clsx";
import Alert from "react-bootstrap/Alert";
import FormCheck from "react-bootstrap/FormCheck";
import Table from "react-bootstrap/Table";
import withPagination from "../enhancers/withPagination";
import withSort from "../enhancers/withSort";
import GridRow from "./GridRow";
import Icon from "./Icon";

export const Grid = props => {
  const {
    page = 1,
    pageSize,
    emptyGridMsg,
    createSelectedRowKey,
    schema = {
      fields: []
    },
    data = [],
    sort = {},
    handleSortChange = () => undefined,
    selectableRows = false,
    allRowsSelected,
    selectedRows = {},
    onRowSelectChange,
    onSelectAllRowsChange,
    checkRowSelected,
    GridRowComponent = GridRow,
    ReanalyzedGridRowComponent,
    expandedRows,
    checkRowExpanded,
    renderExpandedRow,
    onRowExpandedChange,
    expandedRowColumns = schema.fields.length + 1,
    gridErrorMsg,
    gridRowCheckboxDisabled = false,
    rowClassName,
    checkCheckboxVisible,
    isBulkCheckboxVisible = true,
    testId,
    tableProps = {},
    showTableHead = true,
    styleVariant,
    shouldMarkRow,
    cellClassName,
    headerCellClassName
  } = props;

  const bulkSelectionChecked =
    (selectableRows &&
      Boolean(data?.length) &&
      data.reduce(
        (acc, row, index) =>
          acc && Boolean(selectedRows[createSelectedRowKey(index, page, row)]),
        true
      )) ||
    allRowsSelected;

  return (
    <Table data-testid={testId} {...tableProps}>
      {showTableHead && (
        <thead>
          <tr>
            {selectableRows && (
              <th
                style={{
                  width: "3%",
                  minWidth: "40px",
                  paddingRight: 0
                }}
                className={clsx(headerCellClassName, cellClassName)}
              >
                {isBulkCheckboxVisible ? (
                  <FormCheck
                    type="checkbox"
                    inline
                    checked={Boolean(bulkSelectionChecked)}
                    onChange={() => onSelectAllRowsChange({ pageOnly: true })}
                    data-testid={"bulk_selection_checkbox"}
                    className={`new-checkbox new-checkbox--table ${
                      bulkSelectionChecked ? "checked" : ""
                    }`}
                  />
                ) : (
                  ""
                )}
              </th>
            )}
            {schema.fields.map(field => {
              const displayTitle = isFunction(field.displayTitle)
                ? field.displayTitle({ allRowsSelected, selectedRows })
                : field.displayTitle;
              const isFieldSortable = field.sortable && data.length > 0;
              return (
                <th
                  key={field.name}
                  className={clsx(
                    headerCellClassName,
                    cellClassName,
                    field.className
                  )}
                  style={field.style || {}}
                >
                  {isFieldSortable ? (
                    <a
                      href="#"
                      data-testid={snakeCase(`sort_${field.name}`)}
                      onClick={e => {
                        handleSortChange(field.name);
                        e.preventDefault();
                      }}
                    >
                      <span className="table__head-link-text">
                        {displayTitle}
                      </span>
                      {sort.field === field.name && (
                        <Icon
                          name={
                            sort.direction === "asc"
                              ? "chevron-up"
                              : "chevron-down"
                          }
                        />
                      )}
                    </a>
                  ) : (
                    displayTitle
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
      )}
      <tbody>
        {gridErrorMsg && (
          <tr>
            <td colSpan={schema.fields.length + 1}>
              <Alert variant="danger" style={{ marginTop: "20px" }}>
                <p>{gridErrorMsg}</p>
              </Alert>
            </td>
          </tr>
        )}
        {!data?.length && emptyGridMsg && !gridErrorMsg && !props.showLoading && (
          <tr>
            <td colSpan={schema.fields.length + 1}>{emptyGridMsg}</td>
          </tr>
        )}
        {props.showLoading ? (
          <tr>
            <td colSpan={schema.fields.length + 1}>{props.LoadingComponent}</td>
          </tr>
        ) : (
          <></>
        )}
        {data?.length > 0 &&
          !gridErrorMsg &&
          !props.showLoading &&
          data.map((item, itemIndex) => {
            if (
              item?.reanalyzing ||
              ["ERROR", "PROCESSING"].includes(
                item.string_sample_processing_status
              )
            ) {
              return (
                <ReanalyzedGridRowComponent
                  key={itemIndex}
                  rowIndex={itemIndex}
                  rowItem={item}
                  fields={schema.fields}
                  selectableRows={selectableRows}
                  onRowSelectChange={onRowSelectChange}
                  checkRowSelected={checkRowSelected}
                  expandedRows={expandedRows}
                  onRowExpandedChange={onRowExpandedChange}
                  checkRowExpanded={checkRowExpanded}
                  renderExpandedRow={renderExpandedRow}
                  expandedRowColumns={expandedRowColumns}
                  checkboxDisabled={gridRowCheckboxDisabled}
                  rowClassName={rowClassName}
                  checkCheckboxVisible={checkCheckboxVisible}
                />
              );
            }
            return (
              <GridRowComponent
                key={itemIndex}
                rowIndex={itemIndex}
                rowItem={item}
                fields={schema.fields}
                selectableRows={selectableRows}
                onRowSelectChange={onRowSelectChange}
                checkRowSelected={checkRowSelected}
                expandedRows={expandedRows}
                onRowExpandedChange={onRowExpandedChange}
                checkRowExpanded={checkRowExpanded}
                renderExpandedRow={renderExpandedRow}
                expandedRowColumns={expandedRowColumns}
                checkboxDisabled={gridRowCheckboxDisabled}
                rowClassName={rowClassName}
                checkCheckboxVisible={checkCheckboxVisible}
                shouldMarkRow={shouldMarkRow}
                cellClassName={cellClassName}
              />
            );
          })}
      </tbody>
    </Table>
  );
};

Grid.propTypes = {
  showLoading: PropTypes.bool,
  LoadingComponent: PropTypes.node,
  children: PropTypes.node,
  tableProps: PropTypes.shape({ ...Table.propTypes }),
  page: PropTypes.number,
  pageSize: PropTypes.number,
  createSelectedRowKey: PropTypes.func,
  sort: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.string
  }),
  selectableRows: PropTypes.bool,
  allRowsSelected: PropTypes.bool,
  onRowSelectChange: PropTypes.func,
  onSelectAllRowsChange: PropTypes.func,
  checkRowSelected: PropTypes.func,
  selectedRows: PropTypes.shape(),
  checkRowExpanded: PropTypes.func,
  renderExpandedRow: PropTypes.func,
  onRowExpandedChange: PropTypes.func,
  expandedRows: PropTypes.shape({}),
  expandedRowColumns: PropTypes.number,
  schema: PropTypes.shape({
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        sortable: PropTypes.bool,
        name: PropTypes.string,
        displayTitle: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.func,
          PropTypes.element
        ]),
        component: PropTypes.oneOfType([PropTypes.func, PropTypes.element])
      })
    )
  }),
  data: PropTypes.arrayOf(PropTypes.object),
  handleSortChange: PropTypes.func,
  GridRowComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  ReanalyzedGridRowComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func
  ]),
  gridErrorMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  emptyGridMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  gridRowCheckboxDisabled: PropTypes.bool,
  isBulkCheckboxVisible: PropTypes.bool,
  rowClassName: PropTypes.func,
  checkCheckboxVisible: PropTypes.func,
  testId: PropTypes.string,
  showTableHead: PropTypes.bool,
  styleVariant: PropTypes.oneOf(["basic", "bordered"]),
  shouldMarkRow: PropTypes.func,
  cellClassName: PropTypes.string,
  headerCellClassName: PropTypes.string
};

export const PaginatedGrid = withPagination(Grid);
export const SortedGrid = withSort(Grid);

export default withPagination(withSort(Grid));
